import axios from "axios";
import { store } from "./store/store";
import { refreshTokenSuccess, logout } from "./store/authSlice";

const API_URL = process.env.REACT_APP_API_URL;

// Single axios instance for the whole app
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  
});
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state?.auth?.accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);


let isRefreshing = false;
let queue = [];

const resolveQueue = (error, token = null) => {
  queue.forEach((p) => (token ? p.resolve(token) : p.reject(error)));
  queue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;

      const state = store.getState();
      const refreshToken = state?.auth?.refreshToken;

      if (!refreshToken) {
        store.dispatch(logout());
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait until the current refresh finishes, then retry
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: (newAccess) => {
              original.headers.Authorization = `Bearer ${newAccess}`;
              resolve(api(original));
            },
            reject: (err) => reject(err),
          });
        });
      }

      try {
        isRefreshing = true;

        // Call your refresh endpoint (adjust path/body as your backend expects)
        const { data } = await axios.post(
          `${API_URL}/token/refresh`,
          { refresh_token: refreshToken },
          { headers: { Authorization: `Bearer ${refreshToken}` } }
        );

        const newAccess = data?.access_token;
        if (!newAccess) throw new Error("No access_token in refresh response");

        // Persist in Redux (which also writes to localStorage)
        store.dispatch(refreshTokenSuccess({ access_token: newAccess }));

        resolveQueue(null, newAccess);

        // retry original with new token
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (refreshErr) {
        resolveQueue(refreshErr, null);
        if (refreshErr.response?.status === 401 || refreshErr.response?.status === 422) {
          store.dispatch(logout());
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const headerConfig = {
  headers: { "Content-Type": "application/json" },
};

export default api;
